import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { fK, fX } from '../utils/formatters';
// ── Tunable thresholds ────────────────────────────────────────────────────
const RECOVERY_ROAS_THRESHOLD = 1.0; // Post-threshold ROAS > 1x = "actually profitable"
const STRONG_RECOVERY_ROAS = 2.0; // > 2x = "clearly recovered"
const MIN_AFTER_SPEND_FOR_RECOVERY = 50; // < $50 sample too small
const MIN_PASS_FOR_PROJECTIONS = 5; // < 5 Pass ads = unreliable projection target
const MIN_PASS_SPEND_RATIO = 0.05; // < 5% Pass spend = unreliable redirect target
// ── Pure compute ───────────────────────────────────────────────────────────
function computeAuditInsights(report) {
    const ads = report?.ads || [];
    const counts = report?.counts || {};
    const proj = report?.projections || {};
    const totalSpend = proj.totalSpend || 0;
    const totalRevenue = proj.totalRevenue || 0;
    const actualROAS = proj.actualROAS || 0;
    const passSpend = proj.passSpend || 0;
    const avgPassROAS = proj.avgPassROAS || 0;
    // Recovered fail detection
    const failAds = ads.filter((a) => a.result === 'Fail');
    const recoveredFails = failAds.filter((a) => {
        const aS = a.afterSpend || 0;
        if (aS < MIN_AFTER_SPEND_FOR_RECOVERY)
            return false;
        return ((a.afterRevenue || 0) / aS) > RECOVERY_ROAS_THRESHOLD;
    });
    const stronglyRecoveredFails = failAds.filter((a) => {
        const aS = a.afterSpend || 0;
        if (aS < MIN_AFTER_SPEND_FOR_RECOVERY)
            return false;
        return ((a.afterRevenue || 0) / aS) > STRONG_RECOVERY_ROAS;
    });
    const recoveredAfterRevenue = recoveredFails.reduce((s, a) => s + (a.afterRevenue || 0), 0);
    const recoveredAfterSpend = recoveredFails.reduce((s, a) => s + (a.afterSpend || 0), 0);
    const topRecoveredFails = [...recoveredFails]
        .sort((a, b) => (b.afterRevenue || 0) - (a.afterRevenue || 0))
        .slice(0, 10)
        .map((a) => ({
        ad_id: a.ad_id,
        ad_name: a.ad_name,
        afterSpend: a.afterSpend || 0,
        afterRevenue: a.afterRevenue || 0,
        afterROAS: (a.afterRevenue || 0) / (a.afterSpend || 1),
    }));
    // Confidence
    const passCount = counts.Pass || 0;
    const failCount = counts.Fail || 0;
    const passSpendRatio = totalSpend > 0 ? passSpend / totalSpend : 0;
    const failPostThresholdROAS = proj.failWastedSpend > 0 ? proj.failWastedRev / proj.failWastedSpend : 0;
    const confidenceReasons = [];
    let confidenceLevel = 'high';
    if (passCount === 0) {
        confidenceLevel = 'unavailable';
        confidenceReasons.push({
            text: '0 Pass ads — projection model has no winners to redirect failed-ad spend to.',
        });
    }
    else if (passCount < MIN_PASS_FOR_PROJECTIONS) {
        confidenceLevel = 'low';
        confidenceReasons.push({
            text: `Only ${passCount} Pass ads — sample too small to project optimization gains reliably.`,
        });
    }
    if (passSpendRatio < MIN_PASS_SPEND_RATIO && passSpend > 0) {
        if (confidenceLevel === 'high')
            confidenceLevel = 'low';
        confidenceReasons.push({
            text: `Pass ads receive only ${(passSpendRatio * 100).toFixed(1)}% of total spend — too little to be a confident redirect target.`,
        });
    }
    if (failPostThresholdROAS > avgPassROAS && avgPassROAS > 0) {
        if (confidenceLevel === 'high')
            confidenceLevel = 'low';
        confidenceReasons.push({
            text: `Fail ads' post-threshold ROAS (${failPostThresholdROAS.toFixed(2)}x) exceeds Pass-ad average (${avgPassROAS.toFixed(2)}x) — pausing them would lose more than redirection can replace.`,
        });
    }
    if (recoveredFails.length > 0 && failCount > 0) {
        const ratio = recoveredFails.length / failCount;
        if (ratio > 0.2 && confidenceLevel === 'high')
            confidenceLevel = 'low';
        if (ratio > 0.1) {
            confidenceReasons.push({
                text: `${recoveredFails.length} of ${failCount} Fail ads (${Math.round(ratio * 100)}%) have profitable post-threshold ROAS — consider reclassifying before projecting.`,
            });
        }
    }
    // Failure mode breakdown
    const ocpcFailCount = ads.filter((a) => a.ocpcRating === 'Fail').length;
    const octrFailCount = ads.filter((a) => a.octrRating === 'Fail').length;
    const roasFailCount = ads.filter((a) => a.roasRating === 'Fail').length;
    const recommendation = buildRecommendation({
        confidenceLevel,
        passCount,
        failCount,
        learningCount: counts.Learning || 0,
        recoveredCount: recoveredFails.length,
        recoveredAfterRevenue,
        ads_count: report?.ads_count || ads.length,
    });
    const findings = buildFindings({
        confidenceLevel,
        counts,
        passCount,
        failCount,
        learningCount: counts.Learning || 0,
        learningRatio: ads.length > 0 ? (counts.Learning || 0) / ads.length : 0,
        recoveredFails,
        stronglyRecoveredFails,
        recoveredAfterRevenue,
        recoveredAfterSpend,
        actualROAS,
        avgPassROAS,
        passSpendRatio,
        failPostThresholdROAS,
        ocpcFailCount,
        octrFailCount,
        roasFailCount,
        optWastedSpend: proj.optWastedSpend || 0,
        totalSpend,
        totalRevenue,
    });
    return { confidenceLevel, confidenceReasons, recommendation, findings, topRecoveredFails };
}
function buildRecommendation(c) {
    if (c.confidenceLevel === 'unavailable') {
        const learningPct = c.ads_count > 0 ? (c.learningCount / c.ads_count) * 100 : 0;
        const steps = [];
        if (c.recoveredCount > 0) {
            steps.push({
                title: `Walk client through the ${c.failCount} Fail ads`,
                detail: `${c.recoveredCount} of them have profitable post-threshold ROAS — they're misclassified, not failing. Highlighting these recovered ads is the strongest message in this audit (${fK(c.recoveredAfterRevenue)} of post-threshold revenue from "failures").`,
            });
        }
        if (learningPct > 30) {
            steps.push({
                title: `${Math.round(learningPct)}% of inventory is still in Learning phase`,
                detail: `${c.learningCount} of ${c.ads_count} ads haven't crossed threshold. Account is too early-stage for projection modeling. Recommend re-running this audit in 60-90 days once Learning ads stabilize.`,
            });
        }
        steps.push({
            title: 'Pivot the conversation away from optimization scenarios',
            detail: 'Lead with diagnostic findings (creative testing patterns, OCPC failure severity, threshold tuning) instead of "pause and redirect." Set expectation that scenario-based projections will be meaningful in the next audit cycle.',
        });
        return {
            headline: 'This account is not yet a fit for projection modeling.',
            body: `With ${c.passCount} Pass ads, the standard "pause failures, redirect to winners" model has no redirect target. Lead the audit call with diagnostic findings instead of scenarios.`,
            steps,
        };
    }
    if (c.confidenceLevel === 'low') {
        const steps = [];
        if (c.recoveredCount > 0) {
            steps.push({
                title: `Lead with the ${c.recoveredCount} recovered Fail ads`,
                detail: `Out of ${c.failCount} "fails," ${c.recoveredCount} are actually performing post-threshold (${fK(c.recoveredAfterRevenue)} in revenue). The simplistic projection would tell the client to walk away from this revenue. Re-run scenarios after reclassifying.`,
            });
        }
        steps.push({
            title: 'Position optimization as creative-testing improvements, not pause-and-redirect',
            detail: 'Show the OCPC failure severity breakdown and creative pattern findings. The real spend-efficiency gains in this account come from upstream testing process improvements — not from pausing the current Fail cohort.',
        });
        steps.push({
            title: 'Save scenario projections for the appendix',
            detail: 'Frame them as "what if we reclassified the recovered fails first." This resets the projection math on a cleaner Pass/Fail split and gives more defensible scenario numbers.',
        });
        return {
            headline: 'Show projections — but lead with the diagnostic story.',
            body: 'Standard projection scenarios will look flat in this report because the Pass/Fail classification doesn\'t reflect actual recent performance. Use the recovered-ads finding as the headline.',
            steps,
        };
    }
    const steps = [
        { title: 'Lead with the wasted-spend headline', detail: 'The Conservative scenario shows the most defensible upside.' },
    ];
    if (c.recoveredCount > 0) {
        steps.push({
            title: `Mention the ${c.recoveredCount} recovered Fail ads as a credibility note`,
            detail: 'Acknowledging them shows analytical rigor and pre-empts pushback when the client reviews their own data.',
        });
    }
    steps.push({
        title: 'Close with Full Potential as the aspirational ceiling',
        detail: 'Show that Conservative is the floor, Full Potential is the ceiling. The real outcome usually lands in Balanced.',
    });
    return {
        headline: 'Standard audit narrative applies — projections are reliable.',
        body: `${c.passCount} Pass ads provide a confident redirect target for failed spend. Scenario projections can be presented as-is.`,
        steps,
    };
}
function buildFindings(c) {
    const f = [];
    if (c.recoveredFails.length > 0) {
        f.push({
            severity: c.recoveredFails.length / Math.max(c.failCount, 1) > 0.2 ? 'warn' : 'info',
            title: `${c.recoveredFails.length} "Fail" ads have recovered post-threshold`,
            detail: `These ads scored Fail at threshold but their post-threshold ROAS is profitable (>1x). Combined revenue: ${fK(c.recoveredAfterRevenue)} on ${fK(c.recoveredAfterSpend)} spend (${(c.recoveredAfterRevenue / Math.max(c.recoveredAfterSpend, 1)).toFixed(2)}x ROAS). Manually review before pausing.`,
        });
    }
    if (c.stronglyRecoveredFails.length > 0 &&
        c.stronglyRecoveredFails.length !== c.recoveredFails.length) {
        f.push({
            severity: 'warn',
            title: `${c.stronglyRecoveredFails.length} of those have post-threshold ROAS over 2x`,
            detail: 'These are the strongest "misclassified Fail" candidates. Reclassifying them as Pass would meaningfully shift the projection scenarios.',
        });
    }
    if (c.passCount === 0) {
        f.push({
            severity: 'danger',
            title: 'Account has 0 Pass ads',
            detail: 'No ads have passed threshold with favorable OCPC/OCTR/ROAS ratings. The account is either too young, has structural creative issues, or the threshold needs tuning.',
        });
    }
    else if (c.passSpendRatio < MIN_PASS_SPEND_RATIO) {
        f.push({
            severity: 'warn',
            title: `Pass ads receive only ${(c.passSpendRatio * 100).toFixed(1)}% of spend`,
            detail: `${c.passCount} Pass ads exist, but the budget is mostly going elsewhere. Reallocate budget toward winners or grow the Pass cohort through creative refresh.`,
        });
    }
    if (c.learningRatio > 0.3) {
        f.push({
            severity: c.learningRatio > 0.5 ? 'warn' : 'info',
            title: `${Math.round(c.learningRatio * 100)}% of inventory in Learning phase`,
            detail: `${c.learningCount} ads haven't crossed the threshold yet. Account needs more spend volume per ad before classification stabilizes — consider concentrating budget on top testing creatives instead of spreading thin.`,
        });
    }
    if (c.failPostThresholdROAS > c.avgPassROAS && c.avgPassROAS > 0 && c.failPostThresholdROAS > 1) {
        f.push({
            severity: 'warn',
            title: 'Fail ads\' post-threshold performance exceeds Pass-ad average',
            detail: `Fail post-threshold ROAS (${c.failPostThresholdROAS.toFixed(2)}x) > Pass at-threshold ROAS (${c.avgPassROAS.toFixed(2)}x). The "Fail" classification reflects a moment in time; recent performance has shifted.`,
        });
    }
    const total = (c.counts.Pass || 0) + (c.counts.Optimize || 0) + (c.counts.Fail || 0);
    if (total > 0) {
        const ocpcRatio = c.ocpcFailCount / total;
        const roasRatio = c.roasFailCount / total;
        if (ocpcRatio > 0.3) {
            f.push({
                severity: 'info',
                title: `OCPC is the dominant failure mode (${Math.round(ocpcRatio * 100)}% of evaluated ads)`,
                detail: `${c.ocpcFailCount} of ${total} evaluated ads have a failing OCPC rating. This is a creative-cost issue more than an audience or revenue issue — refresh hooks/visuals before tweaking targeting.`,
            });
        }
        else if (roasRatio > 0.3) {
            f.push({
                severity: 'info',
                title: `ROAS is the dominant failure mode (${Math.round(roasRatio * 100)}% of evaluated ads)`,
                detail: `${c.roasFailCount} of ${total} evaluated ads have a failing ROAS rating despite acceptable cost metrics. This is a conversion-side issue — review landing pages, offer, audience match.`,
            });
        }
    }
    if (c.optWastedSpend > 0 && c.totalSpend > 0 && c.optWastedSpend / c.totalSpend > 0.05) {
        f.push({
            severity: 'info',
            title: `${fK(c.optWastedSpend)} in Optimize-tier wasted spend`,
            detail: 'Optimize ads continue spending post-threshold despite mixed ratings. These are real candidates for creative refresh or audience adjustment, not pausing.',
        });
    }
    if (c.actualROAS >= 1.5) {
        f.push({
            severity: 'success',
            title: `Account-blended ROAS is ${c.actualROAS.toFixed(2)}x`,
            detail: 'Despite individual-ad variance, the account is profitable in aggregate. Optimization opportunity is structural rather than acute.',
        });
    }
    else if (c.actualROAS < 1.0 && c.totalSpend > 0) {
        f.push({
            severity: 'danger',
            title: `Account-blended ROAS is ${c.actualROAS.toFixed(2)}x — losing money`,
            detail: `Total revenue (${fK(c.totalRevenue)}) is below total spend (${fK(c.totalSpend)}). This account needs structural intervention before scaling.`,
        });
    }
    return f;
}
// ── Severity / confidence palette ─────────────────────────────────────────
const SEV_PALETTE = {
    success: { bg: '#ECFDF3', bd: '#ABEFC6', tx: '#085D3A', ic: '✓' },
    info: { bg: '#EFF8FF', bd: '#B2DDFF', tx: '#175CD3', ic: 'i' },
    warn: { bg: '#FFFAEB', bd: '#FEDF89', tx: '#B54708', ic: '!' },
    danger: { bg: '#FEF3F2', bd: '#FECDCA', tx: '#B42318', ic: '!' },
};
const CONFIDENCE_COPY = {
    high: { bg: '#ECFDF3', bd: '#ABEFC6', tx: '#085D3A', title: 'High confidence — projections are reliable' },
    low: { bg: '#FFFAEB', bd: '#FEDF89', tx: '#B54708', title: 'Low confidence — review caveats below' },
    unavailable: { bg: '#FEF3F2', bd: '#FECDCA', tx: '#B42318', title: 'Projection scenarios disabled — diagnostic mode' },
};
// ── React component ───────────────────────────────────────────────────────
const InsightsTab = ({ report }) => {
    const insights = useMemo(() => computeAuditInsights(report), [report]);
    const conf = CONFIDENCE_COPY[insights.confidenceLevel];
    return (_jsxs(Box, { sx: { p: 3, display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsxs(Paper, { elevation: 0, sx: {
                    p: 2,
                    borderRadius: '8px',
                    bgcolor: conf.bg,
                    border: `1px solid ${conf.bd}`,
                }, children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 700, color: conf.tx, mb: 0.75 }, children: conf.title }), insights.confidenceReasons.length > 0 && (_jsx(Box, { component: "ul", sx: { m: 0, pl: 2.5, fontSize: 12, lineHeight: 1.6, color: conf.tx }, children: insights.confidenceReasons.map((r, i) => (_jsx("li", { children: r.text }, i))) }))] }), _jsxs(Paper, { elevation: 0, sx: {
                    p: 2.25,
                    borderRadius: '8px',
                    bgcolor: '#EFF8FF',
                    border: '1px solid #B2DDFF',
                }, children: [_jsx(Typography, { sx: {
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#175CD3',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            mb: 0.75,
                        }, children: "Recommendation for this audit call" }), _jsx(Typography, { sx: { fontSize: 14, fontWeight: 700, color: '#175CD3', mb: 0.5 }, children: insights.recommendation.headline }), _jsx(Typography, { sx: { fontSize: 12, color: '#175CD3', lineHeight: 1.6, mb: 1.5 }, children: insights.recommendation.body }), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.25 }, children: insights.recommendation.steps.map((step, i) => (_jsxs(Box, { sx: { display: 'flex', gap: 1.5, alignItems: 'flex-start' }, children: [_jsx(Box, { sx: {
                                        width: 22,
                                        height: 22,
                                        borderRadius: '50%',
                                        bgcolor: '#175CD3',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        flexShrink: 0,
                                    }, children: i + 1 }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { sx: { fontSize: 12.5, fontWeight: 700, color: '#175CD3', mb: 0.25 }, children: step.title }), _jsx(Typography, { sx: { fontSize: 11.5, color: '#175CD3', lineHeight: 1.55 }, children: step.detail })] })] }, i))) })] }), insights.findings.length > 0 && (_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 700, color: '#101828', mb: 1 }, children: "Key findings" }), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.75 }, children: insights.findings.map((f, i) => {
                            const c = SEV_PALETTE[f.severity];
                            return (_jsxs(Paper, { elevation: 0, sx: {
                                    p: 1.5,
                                    bgcolor: '#fff',
                                    border: '1px solid #EAECF0',
                                    borderLeft: `3px solid ${c.bd}`,
                                    borderRadius: '8px',
                                    display: 'flex',
                                    gap: 1.5,
                                    alignItems: 'flex-start',
                                }, children: [_jsx(Box, { sx: {
                                            width: 22,
                                            height: 22,
                                            borderRadius: '50%',
                                            bgcolor: c.bg,
                                            color: c.tx,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 11,
                                            fontWeight: 700,
                                            flexShrink: 0,
                                        }, children: c.ic }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 700, color: '#101828', mb: 0.25 }, children: f.title }), _jsx(Typography, { sx: { fontSize: 12, color: '#475467', lineHeight: 1.55 }, children: f.detail })] })] }, i));
                        }) })] })), insights.topRecoveredFails.length > 0 && (_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 700, color: '#101828', mb: 0.5 }, children: "Top recovered \"Fail\" ads" }), _jsxs(Typography, { sx: { fontSize: 11.5, color: '#475467', mb: 1.25 }, children: ["Classified Fail at threshold but post-threshold ROAS exceeds 1x with sample > $", MIN_AFTER_SPEND_FOR_RECOVERY] }), _jsxs(Paper, { elevation: 0, sx: {
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            bgcolor: '#fff',
                        }, children: [_jsxs(Box, { sx: {
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 90px 90px 90px 80px',
                                    gap: 1,
                                    px: 1.75,
                                    py: 1.25,
                                    bgcolor: '#F9FAFB',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#475467',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                }, children: [_jsx("span", { children: "Ad name" }), _jsx("span", { style: { textAlign: 'right' }, children: "After Spend" }), _jsx("span", { style: { textAlign: 'right' }, children: "After Rev" }), _jsx("span", { style: { textAlign: 'right' }, children: "After ROAS" }), _jsx("span", { style: { textAlign: 'right' }, children: "At Rating" })] }), insights.topRecoveredFails.map((ad) => {
                                const isStrong = ad.afterROAS >= STRONG_RECOVERY_ROAS;
                                return (_jsxs(Box, { sx: {
                                        display: 'grid',
                                        gridTemplateColumns: '2fr 90px 90px 90px 80px',
                                        gap: 1,
                                        px: 1.75,
                                        py: 1.25,
                                        borderTop: '1px solid #F2F4F7',
                                        fontSize: 12,
                                        alignItems: 'center',
                                    }, children: [_jsx(Typography, { sx: {
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#101828',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }, title: ad.ad_name, children: ad.ad_name }), _jsx(Typography, { sx: { textAlign: 'right', color: '#475467' }, children: fK(ad.afterSpend) }), _jsx(Typography, { sx: { textAlign: 'right', color: '#475467', fontWeight: 600 }, children: fK(ad.afterRevenue) }), _jsx(Typography, { sx: {
                                                textAlign: 'right',
                                                color: isStrong ? '#085D3A' : '#175CD3',
                                                fontWeight: 700,
                                            }, children: fX(ad.afterROAS) }), _jsx(Box, { sx: { textAlign: 'right' }, children: _jsx(Chip, { label: "Fail", size: "small", sx: {
                                                    bgcolor: '#FEF3F2',
                                                    color: '#B42318',
                                                    fontWeight: 700,
                                                    fontSize: 10.5,
                                                    height: 20,
                                                    '& .MuiChip-label': { px: 1 },
                                                } }) })] }, ad.ad_id));
                            })] })] }))] }));
};
export default InsightsTab;
export { computeAuditInsights };
